import { useState, useEffect } from "react";
import { api } from "@/api/client";
import { fmtDate } from "@/components/common";
import { toast } from "sonner";
import { Search, Send, Bot, User as UserIcon, Loader2, Brain, MessageCircle, X, Trash2 } from "lucide-react";
import { confirmDialog } from "@/components/ui/confirm";

const SUGGESTED = [
  "What tasks are delayed?",
  "Which team member is overloaded?",
  "Show this month's performance.",
  "Which recurring tasks are failing?",
];

export default function MobileMemory() {
  const [tab, setTab] = useState("chat");

  return (
    <div className="flex flex-col h-full">
      {/* Tab toggle */}
      <div className="px-4 pt-3 pb-2 flex-shrink-0">
        <div className="flex bg-slate-100 rounded-xl p-1 gap-1">
          {[["chat", "AI Chat", MessageCircle], ["search", "Memory Search", Brain]].map(([key, label, Icon]) => (
            <button key={key} onClick={() => setTab(key)}
              className={`flex-1 py-2 rounded-lg text-xs font-semibold transition-all flex items-center justify-center gap-1.5 ${
                tab === key ? "bg-white text-slate-900 shadow-sm" : "text-slate-500"
              }`}>
              <Icon size={13} /> {label}
            </button>
          ))}
        </div>
      </div>

      {tab === "chat" ? <MobileChatTab /> : <MobileSearchTab />}
    </div>
  );
}

function MobileChatTab() {
  const [messages, setMessages] = useState([
    { role: "assistant", content: "Hi! I'm SKIVERSE Task App. Ask me anything about tasks, approvals, or team performance." }
  ]);
  const [input, setInput] = useState("");
  const [loading, setLoading] = useState(false);

  const send = async (text) => {
    const q = text || input;
    if (!q.trim()) return;
    setMessages(m => [...m, { role: "user", content: q }]);
    setInput("");
    setLoading(true);
    try {
      const r = await api.post("/memories/chat", { question: q });
      const ans = r.data?.data;
      setMessages(m => [...m, { role: "assistant", content: ans?.answer || "No answer.", sources_count: ans?.sources_count }]);
    } catch {
      setMessages(m => [...m, { role: "assistant", content: "Sorry, I couldn't process that." }]);
    } finally { setLoading(false); }
  };

  return (
    <div className="flex flex-col flex-1 overflow-hidden">
      {/* Messages */}
      <div className="flex-1 overflow-y-auto px-4 py-2 space-y-3">
        {messages.map((m, i) => (
          <div key={i} className={`flex gap-2.5 ${m.role === "user" ? "justify-end" : ""}`}>
            {m.role !== "user" && (
              <div className="w-7 h-7 rounded-full bg-slate-900 flex items-center justify-center text-white flex-shrink-0 mt-0.5">
                <Bot size={13} />
              </div>
            )}
            <div className={`max-w-[80%] px-3.5 py-2.5 rounded-2xl text-sm leading-relaxed ${
              m.role === "user"
                ? "bg-[#334E7B] text-white rounded-tr-sm"
                : "bg-white border border-slate-100 text-slate-800 rounded-tl-sm"
            }`}>
              <div className="whitespace-pre-wrap">{m.content}</div>
              {m.sources_count !== undefined && (
                <div className="text-[10px] opacity-60 mt-1">{m.sources_count} memories searched</div>
              )}
            </div>
            {m.role === "user" && (
              <div className="w-7 h-7 rounded-full bg-slate-200 flex items-center justify-center flex-shrink-0 mt-0.5">
                <UserIcon size={13} className="text-slate-600" />
              </div>
            )}
          </div>
        ))}
        {loading && (
          <div className="flex gap-2.5">
            <div className="w-7 h-7 rounded-full bg-slate-900 flex items-center justify-center text-white flex-shrink-0"><Bot size={13}/></div>
            <div className="bg-white border border-slate-100 px-3.5 py-2.5 rounded-2xl rounded-tl-sm flex items-center gap-2 text-sm text-slate-400">
              <Loader2 size={13} className="animate-spin" /> Thinking...
            </div>
          </div>
        )}
      </div>

      {/* Suggested prompts (initial state) */}
      {messages.length <= 1 && (
        <div className="px-4 pb-2 flex flex-wrap gap-2">
          {SUGGESTED.map((s, i) => (
            <button key={i} onClick={() => send(s)}
              className="text-xs bg-white border border-slate-200 text-slate-600 px-3 py-1.5 rounded-full active:bg-slate-50">
              {s}
            </button>
          ))}
        </div>
      )}

      {/* Input */}
      <div className="px-4 pb-4 pt-2 flex-shrink-0 flex gap-2">
        <input className="flex-1 px-3.5 py-2.5 text-sm bg-white border border-slate-200 rounded-xl focus:outline-none focus:border-[#334E7B] focus:ring-2 focus:ring-[#334E7B]/20"
          placeholder="Ask anything..."
          value={input}
          onChange={e => setInput(e.target.value)}
          onKeyDown={e => e.key === "Enter" && !e.shiftKey && (e.preventDefault(), send())}
          disabled={loading}
        />
        <button onClick={() => send()} disabled={loading || !input.trim()}
          className="w-10 h-10 flex-shrink-0 bg-[#334E7B] text-white rounded-xl flex items-center justify-center disabled:opacity-40 active:scale-95 transition-transform">
          {loading ? <Loader2 size={16} className="animate-spin" /> : <Send size={16} />}
        </button>
      </div>
    </div>
  );
}

function MobileSearchTab() {
  const [search, setSearch] = useState("");
  const [category, setCategory] = useState("");
  const [rows, setRows] = useState([]);
  const [loading, setLoading] = useState(false);

  const CATS = ["Task","Team","Client","Approval","Project","WhatsApp","Delay","Performance","Other"];

  const load = async () => {
    setLoading(true);
    try {
      const params = {};
      if (search) params.search = search;
      if (category) params.category = category;
      const r = await api.get("/memories", { params });
      setRows(r.data?.data || []);
    } catch {} finally { setLoading(false); }
  };

  useEffect(() => { load(); }, [category]); // eslint-disable-line

  const remove = async (id) => {
    if (!(await confirmDialog({ title: "Delete memory", message: "Delete this memory?", confirmText: "Delete", danger: true }))) return;
    try { await api.delete(`/memories/${id}`); toast.success("Deleted"); load(); }
    catch (e) { toast.error(e?.response?.data?.message || "Failed"); }
  };

  return (
    <div className="flex flex-col flex-1 overflow-hidden">
      <div className="px-4 space-y-2 pb-2 flex-shrink-0">
        <div className="relative">
          <Search size={14} className="absolute left-3 top-1/2 -translate-y-1/2 text-slate-400"/>
          <input value={search} onChange={e => setSearch(e.target.value)}
            onKeyDown={e => e.key === "Enter" && load()}
            placeholder="Search memories..."
            className="w-full pl-9 pr-8 py-2.5 text-sm bg-white border border-slate-200 rounded-xl focus:outline-none focus:border-[#334E7B]" />
          {search && <button onClick={() => { setSearch(""); setTimeout(load, 0); }} className="absolute right-3 top-1/2 -translate-y-1/2 text-slate-400"><X size={13}/></button>}
        </div>
        <div className="flex gap-1.5 overflow-x-auto scrollbar-hide pb-1">
          <button onClick={() => setCategory("")}
            className={`flex-shrink-0 px-3 py-1.5 rounded-full text-xs font-semibold border transition-all ${category === "" ? "bg-blue-50 text-blue-700 border-blue-200" : "bg-white text-slate-500 border-slate-200"}`}>
            All
          </button>
          {CATS.map(c => (
            <button key={c} onClick={() => setCategory(c === category ? "" : c)}
              className={`flex-shrink-0 px-3 py-1.5 rounded-full text-xs font-semibold border transition-all ${category === c ? "bg-blue-50 text-blue-700 border-blue-200" : "bg-white text-slate-500 border-slate-200"}`}>
              {c}
            </button>
          ))}
        </div>
      </div>

      <div className="flex-1 overflow-y-auto px-4 pb-4 space-y-2.5">
        {loading ? (
          <div className="flex justify-center pt-8"><Loader2 className="animate-spin text-slate-400" /></div>
        ) : rows.length === 0 ? (
          <div className="text-center pt-10 text-sm text-slate-400">No memories found</div>
        ) : rows.map(m => (
          <div key={m.id} className="bg-white rounded-2xl border border-slate-100 p-3.5">
            <div className="flex items-start justify-between gap-2 mb-1.5">
              <div className="text-sm font-semibold text-slate-900">{m.title}</div>
              <button onClick={() => remove(m.id)} className="p-1 text-red-400 hover:text-red-600 flex-shrink-0">
                <Trash2 size={14} />
              </button>
            </div>
            <div className="flex items-center gap-1 mb-2 flex-wrap">
              <span className="badge-base bg-blue-50 text-blue-700 border-blue-200">{m.category}</span>
              {(m.tags || []).slice(0, 3).map((t, i) => <span key={i} className="badge-base bg-slate-50 text-slate-500 border-slate-200">{t}</span>)}
            </div>
            <div className="text-xs text-slate-600 line-clamp-2 leading-relaxed">{m.summary || m.content}</div>
            <div className="text-[10px] text-slate-400 mt-2">{fmtDate(m.created_at)}</div>
          </div>
        ))}
      </div>
    </div>
  );
}
